import React, { Component, ReactNode } from 'react';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error?: Error;
    errorInfo?: React.ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('ErrorBoundary caught:', error, errorInfo);
        this.setState({ error, errorInfo });
    }

    handleReload = () => {
        window.location.reload();
    };

    handleClearCache = () => {
        try {
            localStorage.clear();
            sessionStorage.clear();
            window.location.reload();
        } catch (e) {
            console.error('Failed to clear cache:', e);
            window.location.reload();
        }
    };

    render() {
        if (this.state.hasError) {
            return (
                <div style={{
                    padding: '40px',
                    fontFamily: 'Arial, sans-serif',
                    maxWidth: '800px',
                    margin: '0 auto',
                    maxHeight: '100vh',
                    overflowY: 'auto',
                    boxSizing: 'border-box'
                }}>
                    <h1 style={{ color: '#d32f2f' }}>⚠️ Application Error</h1>
                    <p>The application encountered an unexpected error. Please try one of the following:</p>
                    
                    <div style={{ margin: '20px 0' }}>
                        <button 
                            onClick={this.handleReload}
                            style={{
                                padding: '10px 20px',
                                marginRight: '10px',
                                backgroundColor: '#1976d2',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}>
                            Reload Page
                        </button>
                        <button 
                            onClick={this.handleClearCache}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#f57c00',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '14px'
                            }}>
                            Clear Cache & Reload
                        </button>
                    </div>

                    <details style={{ marginTop: '20px' }}>
                        <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '10px' }}>
                            Technical Details (for IT support)
                        </summary>
                        <div style={{ 
                            padding: '15px', 
                            backgroundColor: '#f5f5f5', 
                            borderRadius: '4px',
                            fontSize: '12px',
                            maxHeight: '400px',
                            overflowY: 'auto',
                            overflowX: 'auto'
                        }}>
                            <p><strong>Error:</strong> {this.state.error?.message}</p>
                            <pre style={{ 
                                overflow: 'auto', 
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                margin: '10px 0'
                            }}>
                                {this.state.error?.stack}
                            </pre>
                            {this.state.errorInfo && (
                                <>
                                    <p><strong>Component Stack:</strong></p>
                                    <pre style={{ 
                                        overflow: 'auto', 
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        margin: '10px 0'
                                    }}>
                                        {this.state.errorInfo.componentStack}
                                    </pre>
                                </>
                            )}
                            <p><strong>Browser:</strong> {navigator.userAgent}</p>
                            <p><strong>URL:</strong> {window.location.href}</p>
                            <p><strong>Time:</strong> {new Date().toISOString()}</p>
                        </div>
                    </details>

                    <p style={{ marginTop: '20px', fontSize: '14px', color: '#666' }}>
                        If the problem persists, please contact IT support with the technical details above.
                    </p>
                </div>
            );
        }

        return this.props.children;
    }
}
